// src/pages/AboutPage.js
// Wise-style visual language adapted to Trend Genie brand.
// All About Us content is preserved; only the UI skin changes.
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FaGooglePlay,
  FaApple,
  FaBrain,
  FaCheckCircle,
  FaMobileAlt,
  FaLightbulb,
  FaShieldAlt,
  FaUsers,
  FaHeart,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { FiTrendingUp, FiTarget, FiEye, FiZap, FiSmartphone } from 'react-icons/fi';

import { APP_LINKS } from '../utils/constants';
import screen1 from '../assets/screen1.png';
import screen2 from '../assets/screen2.png';
import screen3 from '../assets/screen3.png';
import screen4 from '../assets/screen4.png';
import screen5 from '../assets/screen5.png';
import screen6 from '../assets/screen6.png';
import './AboutPage.css';

const SCREENS = [screen1, screen2, screen3, screen4, screen5, screen6];

/* ─────────────────────────────────────────────────────────
   DESIGN CONSTANTS — Wise adapted to TG palette
───────────────────────────────────────────────────────── */
const N  = '#0d2235';   // navy
const E  = '#29ABE2';   // electric
const Eo = '#1a8fc0';   // electric dark
const OR = '#F7941D';   // orange accent
const FG = '#f0f9ff';   // fog
const PA = '#ffffff';   // paper
const BD = '#1f3446';   // body text
const MT = '#5a7a8f';   // muted text
const BR = 'rgba(13,34,53,0.11)';
const BM = 'rgba(13,34,53,0.20)';
const SL = 'rgba(0,0,0,0.08) 0px 6px 20px 0px';

/* ─────────────────────────────────────────────────────────
   KEYFRAMES
───────────────────────────────────────────────────────── */
const floatY = keyframes`
  0%,100% { transform: translateY(0);    }
  50%      { transform: translateY(-9px); }
`;

/* ─────────────────────────────────────────────────────────
   SHARED PRIMITIVES
───────────────────────────────────────────────────────── */

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  padding: 6px 14px;
  border: 1.5px solid ${BM};
  border-radius: 9999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${BD};
  background: ${FG};
  margin-bottom: 1rem;
`;

const DarkBadge = styled(Badge)`
  background: rgba(41,171,226,0.14);
  border-color: rgba(41,171,226,0.28);
  color: ${E};
`;

const DisplayH1 = styled.h1`
  font-size: clamp(2.65rem, 6vw, 5.4rem) !important;
  font-weight: 800;
  line-height: 0.92 !important;
  letter-spacing: -0.025em;
  color: ${N} !important;
  -webkit-text-fill-color: ${N} !important;
  margin: 0 0 1.3rem;

  em {
    font-style: normal;
    color: ${E} !important;
    -webkit-text-fill-color: ${E} !important;
  }

  @media (max-width: 600px) {
    font-size: clamp(2.2rem, 9vw, 3rem) !important;
    line-height: 0.96 !important;
  }
`;

const SectionH2 = styled.h2`
  font-size: clamp(1.8rem, 3.8vw, 3.2rem) !important;
  font-weight: 700;
  line-height: 1.05 !important;
  letter-spacing: -0.018em;
  color: ${N} !important;
  -webkit-text-fill-color: ${N} !important;
  margin: 0 0 0.9rem;

  em {
    font-style: normal;
    color: ${E} !important;
    -webkit-text-fill-color: ${E} !important;
  }
`;

const SubCopy = styled.p`
  font-size: 1.06rem;
  color: ${MT} !important;
  line-height: 1.72;
  margin: 0;
`;

/* Pill Buttons */
const PillBase = `
  display: inline-flex;
  align-items: center;
  gap: 0.62rem;
  padding: 13px 26px;
  border-radius: 9999px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition: transform 0.14s ease, opacity 0.14s ease;
  svg { font-size: 1.3rem; flex-shrink: 0; }
  .bt { display:flex; flex-direction:column; align-items:flex-start; line-height:1.22; }
  .bs { font-size:0.62rem; font-weight:400; opacity:0.82; }
  .bm { font-size:0.87rem; font-weight:700; }
  &:hover  { transform:translateY(-1px); opacity:0.9; }
  &:active { transform:translateY(0);    opacity:1;   }
`;

const PrimaryPill = styled.a`${PillBase}
  background: ${E};
  color: ${PA};
`;

const OutlinePill = styled.a`${PillBase}
  background: ${PA};
  color: ${N};
  border: 1.5px solid ${BM};
  &:hover { background: ${FG}; opacity:1; }
`;

const WhitePill = styled.a`${PillBase}
  background: ${PA};
  color: ${N};
`;

const GhostPill = styled.a`${PillBase}
  background: rgba(255,255,255,0.08);
  color: ${PA};
  border: 1.5px solid rgba(255,255,255,0.26);
  &:hover { background:rgba(255,255,255,0.16); opacity:1; }
`;

const fUp = {
  hidden:  { opacity:0, y:24 },
  visible: (i=0) => ({
    opacity:1, y:0,
    transition:{ delay:i*0.09, duration:0.5, ease:'easeOut' },
  }),
};

/* ═══════════════════════════════════════════════════════
   ①  HERO
═══════════════════════════════════════════════════════ */

const HeroWrap = styled.section`
  background: ${PA} !important;
  padding: 120px 10% 80px !important;
  overflow: hidden;
  position: relative;
  background-image: radial-gradient(${BR} 1px, transparent 1px) !important;
  background-size: 28px 28px !important;
  background-color: ${PA} !important;

  @media (max-width: 900px) { padding: 90px 5% 60px !important; }
`;

const HeroInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 3.5rem;
    text-align: center;
  }
`;

const HeroLeft = styled.div``;

const HeroSub = styled.p`
  font-size: 1.06rem;
  color: ${MT} !important;
  line-height: 1.74;
  max-width: 480px;
  margin: 0 0 2.2rem;

  @media (max-width: 960px) { margin: 0 auto 2.2rem; }
`;

const HeroBtnRow = styled.div`
  display: flex;
  gap: 0.85rem;
  flex-wrap: wrap;
  margin-bottom: 1.7rem;

  @media (max-width: 960px) { justify-content: center; }
`;

const HeroTrust = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex-wrap: wrap;
  font-size: 13px;
  color: ${MT};

  @media (max-width: 960px) { justify-content: center; }

  .ti {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    svg { color: ${E}; font-size: 0.9rem; }
  }
`;

/* Phone + carousel */
const PhoneFrame = styled.div`
  width: 250px;
  border-radius: 36px;
  border: 2px solid ${BM};
  background: ${PA};
  padding: 9px;
  box-shadow: rgba(0,0,0,0.14) 0 10px 32px, rgba(0,0,0,0.04) 0 40px 40px;
  animation: ${floatY} 7s ease-in-out infinite;
  position: relative;
  margin: 0 auto;

  .inner {
    border-radius: 28px;
    overflow: hidden;
    aspect-ratio: 9 / 19.5;
    background: ${FG};
    border: 1px solid ${BR};
    position: relative;
  }

  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 27px;
    transition: opacity 0.6s ease;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;

  span {
    width: 6px; height: 6px;
    border-radius: 9999px;
    background: ${BM};
    transition: background 0.22s ease, width 0.22s ease;
    cursor: pointer;

    &.active { background: ${E}; width: 18px; }
  }
`;

function HeroCarousel() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % SCREENS.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
      <PhoneFrame>
        <div className="inner">
          {SCREENS.map((s,i) => (
            <img
              key={i}
              src={s}
              alt={`Trend Genie AI crypto market analysis app screen ${i+1}`}
              style={{ opacity: i === idx ? 1 : 0 }}
            />
          ))}
        </div>
      </PhoneFrame>
      <CarouselDots>
        {SCREENS.map((_,i) => (
          <span key={i} className={i===idx?'active':''} onClick={()=>setIdx(i)} aria-label={`Screen ${i+1}`} />
        ))}
      </CarouselDots>
    </div>
  );
}

function AboutHero() {
  return (
    <HeroWrap>
      <HeroInner>
        <HeroLeft>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp}>
            <Badge>About Trend Genie</Badge>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={1}>
            <DisplayH1>
              AI-Powered<br />
              <em>Crypto Analysis</em><br />
              for Everyone.
            </DisplayH1>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={2}>
            <HeroSub>
              Trend Genie is built for crypto users who want to understand the market
              better — not for professional traders with Bloomberg terminals. We make
              AI-powered crypto market analysis simple, clear, and accessible.
            </HeroSub>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={3}>
            <HeroBtnRow>
              <PrimaryPill href={APP_LINKS.googlePlay} target="_blank" rel="noopener noreferrer" aria-label="Get Trend Genie on Google Play">
                <FaGooglePlay aria-hidden="true" />
                <span className="bt">
                  <span className="bs">Get it on</span>
                  <span className="bm">Google Play</span>
                </span>
              </PrimaryPill>
              <OutlinePill href={APP_LINKS.appStore} target="_blank" rel="noopener noreferrer" aria-label="Download Trend Genie on the App Store">
                <FaApple aria-hidden="true" />
                <span className="bt">
                  <span className="bs">Download on the</span>
                  <span className="bm">App Store</span>
                </span>
              </OutlinePill>
            </HeroBtnRow>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={4}>
            <HeroTrust>
              <span className="ti"><FaCheckCircle aria-hidden="true" /> Free to download</span>
              <span className="ti"><FaCheckCircle aria-hidden="true" /> Android &amp; iOS</span>
              <span className="ti"><FaCheckCircle aria-hidden="true" /> No card required</span>
            </HeroTrust>
          </motion.div>
        </HeroLeft>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={2}>
          <HeroCarousel />
        </motion.div>
      </HeroInner>
    </HeroWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ②  ABOUT SECTION
═══════════════════════════════════════════════════════ */

const AboutWrap = styled.section`
  background: ${FG} !important;
`;

const TwoCol = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 2.4rem;
  }
`;

const AboutLeft = styled(motion.div)``;

const StatRow = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const Stat = styled.div`
  .sv { font-size: 1.9rem; font-weight: 800; color: ${N}; line-height: 1; }
  .sl { font-size: 0.82rem; color: ${MT}; margin-top: 0.25rem; }
`;

const FeatureList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const FeatureChip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 14px 16px;
  background: ${PA};
  border: 1px solid ${BR};
  border-radius: 10px;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .fic {
    width: 36px; height: 36px;
    border-radius: 9999px;
    background: ${FG};
    border: 1px solid ${BR};
    color: ${E};
    display: flex; align-items: center; justify-content: center;
    font-size: 0.95rem;
    flex-shrink: 0;
    margin-top: 1px;
  }
  h4 { font-size: 0.92rem !important; font-weight: 700; color: ${N} !important; margin: 0 0 0.2rem; }
  p  { font-size: 0.83rem; color: ${MT} !important; line-height: 1.62; margin: 0; }
`;

const ABOUT_FEATURES = [
  { ic:<FaBrain />,     t:'AI-Powered Analysis',     d:'Our AI processes crypto market data and surfaces the signals that matter most.' },
  { ic:<FiTrendingUp />,t:'Real-Time Market Trends',  d:'Track Bitcoin and altcoin trends as they develop, not after they pass.' },
  { ic:<FaMobileAlt />, t:'Mobile-First Design',      d:'Built for your phone — clean, fast, and accessible anywhere you go.' },
];

function AboutSection() {
  return (
    <AboutWrap>
      <TwoCol>
        <AboutLeft initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp}>
          <Badge>About TG</Badge>
          <SectionH2>What is<br /><em>Trend Genie?</em></SectionH2>
          <SubCopy>
            Trend Genie is an AI-powered crypto market analysis app built for everyday
            crypto users. We combine real-time market data with advanced AI to deliver
            clear, actionable insights on cryptocurrency trends, trading signal patterns,
            and market conditions — all in a simple mobile interface available on
            Android and iOS.
          </SubCopy>
          <StatRow>
            <Stat><div className="sv">AI</div><div className="sl">Market Analysis</div></Stat>
            <Stat><div className="sv">24/7</div><div className="sl">Real-Time Insights</div></Stat>
            <Stat><div className="sv">Free</div><div className="sl">To Download</div></Stat>
          </StatRow>
        </AboutLeft>

        <FeatureList initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={1}>
          {ABOUT_FEATURES.map((f,i) => (
            <FeatureChip key={f.t}>
              <div className="fic">{f.ic}</div>
              <div>
                <h4>{f.t}</h4>
                <p>{f.d}</p>
              </div>
            </FeatureChip>
          ))}
        </FeatureList>
      </TwoCol>
    </AboutWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ③  MISSION SECTION
═══════════════════════════════════════════════════════ */

const MissionWrap = styled.section`
  background: ${PA} !important;
`;

const MissionInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const MissionQuote = styled(motion.blockquote)`
  margin: 2.2rem 0 0;
  padding: 28px 30px;
  background: ${FG};
  border: 1px solid ${BR};
  border-left: 4px solid ${E};
  border-radius: 10px;

  p {
    font-size: 1.08rem !important;
    font-weight: 600;
    color: ${N} !important;
    line-height: 1.7;
    margin: 0;
    font-style: italic;
  }
`;

const MissionPara = styled(motion.p)`
  font-size: 1.02rem !important;
  color: ${MT} !important;
  line-height: 1.78;
  margin: 1.4rem 0 0;
`;

function MissionSection() {
  return (
    <MissionWrap>
      <MissionInner>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp}>
          <Badge>Our Mission</Badge>
          <SectionH2>Democratize<br /><em>crypto market intelligence.</em></SectionH2>
        </motion.div>

        <MissionQuote initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={1}>
          <p>
            "To make professional-grade crypto market analysis accessible to every person
            with a smartphone — not just hedge funds and professional traders."
          </p>
        </MissionQuote>

        <MissionPara initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={2}>
          We believe that everyone who participates in crypto markets deserves access to
          clear, intelligent market analysis — not just those with expensive subscriptions
          or years of technical expertise. Trend Genie was created to level the playing
          field by bringing AI-powered crypto insights directly to your mobile device,
          free to download and use.
        </MissionPara>
      </MissionInner>
    </MissionWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ④  WHY WE BUILT IT
═══════════════════════════════════════════════════════ */

const WhyBuiltWrap = styled.section`
  background: ${FG} !important;
`;

const WhyBuiltInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 2.6rem;
  }
`;

const PainList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    font-size: 0.92rem;
    color: ${BD};
    line-height: 1.62;

    svg { color: ${OR}; font-size: 0.9rem; flex-shrink: 0; margin-top: 3px; }
  }
`;

const PAIN_POINTS = [
  'Too much market noise — endless signals, opinions, and conflicting analysis.',
  'Technical charts that overwhelm traders without deep expertise.',
  'Emotional decisions driven by fear and greed, not data.',
  'No clear way to track multiple cryptocurrencies at once.',
  'Missing key trends because the market moves faster than you can research.',
];

const BuiltCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const BuiltCard = styled(motion.div)`
  background: ${PA};
  border: 1px solid ${BR};
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .bci {
    width: 38px; height: 38px;
    border-radius: 9999px;
    background: ${FG};
    border: 1px solid ${BR};
    color: ${E};
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }
  h4 { font-size: 0.93rem !important; font-weight: 700; color: ${N} !important; margin: 0 0 0.3rem; }
  p  { font-size: 0.83rem; color: ${MT} !important; line-height: 1.62; margin: 0; }
`;

const BUILT_CARDS = [
  { ic:<FaBrain />,    t:'AI Does the Heavy Lifting',  d:"Trend Genie's AI analyzes thousands of data points so you don't have to — surfacing what matters most from the market noise." },
  { ic:<FiTarget />,   t:'Simplified for Action',      d:'Complex market analysis is translated into clear, easy-to-understand insights you can use to make more informed decisions.' },
  { ic:<FaLightbulb />,t:'Built for Everyday Users',   d:"We built Trend Genie for regular crypto users — people who want smarter analysis without needing a PhD in technical analysis." },
];

function WhyBuiltSection() {
  return (
    <WhyBuiltWrap>
      <WhyBuiltInner>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp}>
          <Badge>Why We Built It</Badge>
          <SectionH2>The problem we<br /><em>set out to solve.</em></SectionH2>
          <SubCopy>
            We saw the same challenges repeated across the crypto community. Too much noise,
            too little signal, and analysis tools that were either too complex or too shallow.
          </SubCopy>
          <PainList>
            {PAIN_POINTS.map((p,i) => (
              <li key={i}><FaCheckCircle aria-hidden="true" /> {p}</li>
            ))}
          </PainList>
        </motion.div>

        <BuiltCards>
          {BUILT_CARDS.map((c,i) => (
            <BuiltCard key={c.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.15 }} variants={fUp}>
              <div className="bci">{c.ic}</div>
              <div>
                <h4>{c.t}</h4>
                <p>{c.d}</p>
              </div>
            </BuiltCard>
          ))}
        </BuiltCards>
      </WhyBuiltInner>
    </WhyBuiltWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑤  WHAT MAKES US DIFFERENT
═══════════════════════════════════════════════════════ */

const DiffWrap = styled.section`
  background: ${PA} !important;
`;

const DiffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  max-width: 1100px;
  margin: 2.6rem auto 0;
`;

const DiffCard = styled(motion.div)`
  background: ${FG};
  border: 1px solid ${BR};
  border-radius: 10px;
  padding: 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .di {
    width: 40px; height: 40px;
    border-radius: 9999px;
    background: ${PA};
    border: 1px solid ${BR};
    color: ${E};
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem;
  }
  h3 { font-size: 0.93rem !important; font-weight: 700; color: ${N} !important; margin: 0; }
  p  { font-size: 0.83rem; color: ${MT} !important; line-height: 1.65; margin: 0; }
`;

const DIFF = [
  { ic:<FaBrain />,    t:'AI at the Core',             d:'Every insight in Trend Genie is AI-driven — not curated by humans with their own biases, but generated by models trained on crypto market data.' },
  { ic:<FiZap />,      t:'Speed & Simplicity',          d:'Complex analysis delivered simply. What used to take hours of chart research now takes seconds in Trend Genie.' },
  { ic:<FaUsers />,    t:'For All Experience Levels',   d:"Whether you're a curious beginner or an active daily trader, Trend Genie scales to your needs." },
  { ic:<FiSmartphone />,t:'Mobile-First, Always',       d:'Designed from day one for mobile. No compromises — just a seamless, fast experience on your phone.' },
  { ic:<FaShieldAlt />,t:'Honest & Transparent',        d:'No unrealistic profit guarantees. No hype. Just real AI analysis to support better-informed decisions.' },
  { ic:<FaHeart />,    t:'Built with Passion',           d:'We genuinely care about the crypto community and built Trend Genie to make a real positive difference for everyday traders.' },
];

function DifferenceSection() {
  return (
    <DiffWrap>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <Badge>What Makes Us Different</Badge>
        <SectionH2>How Trend Genie<br /><em>stands apart.</em></SectionH2>
        <SubCopy style={{ maxWidth:520 }}>
          Plenty of apps show charts. Trend Genie helps you understand what those
          charts are actually telling you.
        </SubCopy>
      </div>
      <DiffGrid>
        {DIFF.map((d,i) => (
          <DiffCard key={d.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.1 }} variants={fUp}>
            <div className="di">{d.ic}</div>
            <h3>{d.t}</h3>
            <p>{d.d}</p>
          </DiffCard>
        ))}
      </DiffGrid>
    </DiffWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑥  OUR VISION
═══════════════════════════════════════════════════════ */

const VisionWrap = styled.section`
  background: ${FG} !important;
`;

const VisionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 2.6rem;
  }
`;

const VisionLeft = styled(motion.div)``;

const VisionItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const VisionItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 14px 16px;
  background: ${PA};
  border: 1px solid ${BR};
  border-radius: 10px;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .vic {
    width: 36px; height: 36px;
    border-radius: 9999px;
    background: ${FG};
    border: 1px solid ${BR};
    color: ${E};
    display: flex; align-items: center; justify-content: center;
    font-size: 0.95rem;
    flex-shrink: 0;
    margin-top: 1px;
  }
  h4 { font-size: 0.93rem !important; font-weight: 700; color: ${N} !important; margin: 0 0 0.25rem; }
  p  { font-size: 0.83rem; color: ${MT} !important; line-height: 1.62; margin: 0; }
`;

const VISION_ITEMS = [
  { ic:<FiEye />,       t:'Global Reach',               d:'Making AI crypto analysis accessible to users worldwide, regardless of their background or technical expertise.' },
  { ic:<FaBrain />,     t:'Smarter AI, Continuously',   d:'Constantly improving our AI models to deliver more accurate, more relevant crypto market insights.' },
  { ic:<FiTrendingUp />,t:'Expanding Coverage',          d:'Growing our coverage to include more coins, more markets, and more types of crypto market analysis.' },
];

function VisionSection() {
  return (
    <VisionWrap>
      <VisionInner>
        <VisionLeft initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp}>
          <Badge>Our Vision</Badge>
          <SectionH2>Where we're<br /><em>heading next.</em></SectionH2>
          <SubCopy>
            Our vision is a world where every crypto user — regardless of experience,
            background, or resources — has access to the same quality of market intelligence
            that was previously only available to institutional investors.
          </SubCopy>
          <SubCopy style={{ marginTop:'1rem' }}>
            We're building Trend Genie to be the go-to AI crypto market analysis platform
            for the global retail crypto community — continuously improving, expanding, and
            evolving alongside the market.
          </SubCopy>
        </VisionLeft>

        <VisionItems>
          {VISION_ITEMS.map((v,i) => (
            <VisionItem key={v.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.15 }} variants={fUp}>
              <div className="vic">{v.ic}</div>
              <div>
                <h4>{v.t}</h4>
                <p>{v.d}</p>
              </div>
            </VisionItem>
          ))}
        </VisionItems>
      </VisionInner>
    </VisionWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑦  FINAL CTA — dark navy inverted
═══════════════════════════════════════════════════════ */

const CTAWrap = styled.section`
  background: ${N} !important;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content:'';
    position:absolute;
    top:-40%; left:-10%;
    width:600px; height:600px;
    background: radial-gradient(circle, rgba(41,171,226,0.12) 0%, transparent 70%);
    border-radius:50%;
    pointer-events:none;
  }
`;

const CTAH2 = styled.h2`
  font-size: clamp(2.2rem, 5vw, 4.5rem) !important;
  font-weight: 800;
  line-height: 0.93 !important;
  letter-spacing: -0.022em;
  color: ${PA} !important;
  -webkit-text-fill-color: ${PA} !important;
  margin: 0 0 1.1rem;

  em {
    font-style: normal;
    color: ${E} !important;
    -webkit-text-fill-color: ${E} !important;
  }
`;

const CTASub = styled.p`
  font-size: 1.04rem;
  color: rgba(255,255,255,0.7) !important;
  line-height: 1.72;
  max-width: 490px;
  margin: 0 auto 2.4rem;
`;

const CTABtnRow = styled.div`
  display: flex;
  gap: 0.85rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1.3rem;
`;

const CTANote = styled.p`
  color: rgba(255,255,255,0.38) !important;
  font-size: 12.5px !important;
  margin: 0 !important;
`;

function FinalCTASection() {
  return (
    <CTAWrap>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.3 }} variants={fUp}>
        <DarkBadge>Download Free</DarkBadge>
        <CTAH2>
          Ready to trade with<br />
          <em>more clarity?</em>
        </CTAH2>
        <CTASub>
          Download Trend Genie free today and see how AI-powered crypto market analysis
          can change the way you understand and navigate cryptocurrency markets.
        </CTASub>
        <CTABtnRow>
          <WhitePill href={APP_LINKS.googlePlay} target="_blank" rel="noopener noreferrer" aria-label="Get Trend Genie on Google Play">
            <FaGooglePlay aria-hidden="true" />
            <span className="bt">
              <span className="bs">Get it on</span>
              <span className="bm">Google Play</span>
            </span>
          </WhitePill>
          <GhostPill href={APP_LINKS.appStore} target="_blank" rel="noopener noreferrer" aria-label="Download Trend Genie on the App Store">
            <FaApple aria-hidden="true" />
            <span className="bt">
              <span className="bs">Download on the</span>
              <span className="bm">App Store</span>
            </span>
          </GhostPill>
        </CTABtnRow>
        <CTANote>Free to download · Available on Android &amp; iOS · No card required</CTANote>
      </motion.div>
    </CTAWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑧  DISCLAIMER
═══════════════════════════════════════════════════════ */

const DiscWrap = styled.section`
  background: ${PA} !important;
  padding: 48px 10% !important;
  @media (max-width:768px) { padding: 36px 5% !important; }
`;

const DiscBox = styled.div`
  max-width: 840px;
  margin: 0 auto;
  border: 1px solid rgba(247,148,29,0.35);
  border-left: 4px solid ${OR};
  border-radius: 10px;
  padding: 20px 24px;
  background: #fffcf5;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  .di { font-size: 1.1rem; color:${OR}; flex-shrink:0; margin-top:0.1rem; }
  h3 { font-size: 0.9rem !important; font-weight:700; color:${N} !important; margin:0 0 0.38rem; }
  p  { font-size: 0.82rem; color:${MT} !important; line-height:1.7; margin:0; }

  @media (max-width:560px) { flex-direction:column; gap:0.75rem; padding:16px; }
`;

function DisclaimerBlock() {
  return (
    <DiscWrap>
      <DiscBox>
        <FaExclamationTriangle className="di" aria-hidden="true" />
        <div>
          <h3>Important Disclaimer</h3>
          <p>
            Trend Genie provides market analysis and educational insights only. It does not
            constitute financial advice, investment advice, or trading recommendations.
            Cryptocurrency markets are highly volatile and carry significant risk of loss.
            Always conduct your own research and consult a qualified financial adviser before
            making any trading or investment decisions. Past performance of any analysis is
            not indicative of future results.
          </p>
        </div>
      </DiscBox>
    </DiscWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   PAGE — schema + assembly
═══════════════════════════════════════════════════════ */

const schemaAbout = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Trend Genie — AI Crypto Market Analysis App',
  description: 'Learn about Trend Genie, the AI-powered crypto market analysis app. Discover our mission to democratize crypto intelligence and how we help users make more informed trading decisions.',
  url: 'https://www.trendgenie.io/about',
  isPartOf: { '@type': 'WebSite', url: 'https://www.trendgenie.io' },
};

const schemaOrg = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Trend Genie',
  url: 'https://www.trendgenie.io',
  logo: 'https://www.trendgenie.io/logo.png',
  sameAs: ['https://x.com/trendgenie_ai'],
  description: 'Trend Genie is an AI-powered crypto market analysis platform helping users understand cryptocurrency trends, trading signals, and market insights on Android and iOS.',
  foundingDate: '2024',
  knowsAbout: [
    'AI Crypto Market Analysis', 'Cryptocurrency Trend Detection',
    'Crypto Trading Signal Insights', 'Bitcoin Market Analysis',
    'Altcoin Analysis', 'Technical Analysis', 'Crypto Chart Analysis',
  ],
};

const PageWrap = styled.div``;

const AboutPage = () => (
  <PageWrap>
    <Helmet>
      <title>About Trend Genie | AI-Powered Crypto Market Analysis App</title>
      <meta name="description" content="Learn about Trend Genie — the AI-powered crypto market analysis app for Android and iOS. Discover our mission to make professional crypto market intelligence accessible to every trader." />
      <meta name="keywords" content="about Trend Genie, AI crypto market analysis, crypto trading signals, AI trading assistant, crypto analysis app, cryptocurrency market insights, crypto trend prediction, Bitcoin analysis app, altcoin analysis tool" />
      <link rel="canonical" href="https://www.trendgenie.io/about" />
      <meta property="og:title" content="About Trend Genie | AI Crypto Market Analysis App" />
      <meta property="og:description" content="We built Trend Genie to give every crypto user access to professional-grade AI market analysis — not just institutional investors. Available free on Android and iOS." />
      <meta property="og:image" content="https://www.trendgenie.io/social-share-image.png" />
      <meta property="og:url" content="https://www.trendgenie.io/about" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Trend Genie" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@trendgenie_ai" />
      <meta name="twitter:title" content="About Trend Genie | AI Crypto Market Analysis" />
      <meta name="twitter:description" content="Learn how Trend Genie's AI-powered crypto analysis helps everyday traders understand markets better. Free on Android and iOS." />
      <meta name="twitter:image" content="https://www.trendgenie.io/social-share-image.png" />
      <meta name="robots" content="index, follow" />
      <script type="application/ld+json">{JSON.stringify(schemaAbout)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
    </Helmet>

    <AboutHero />
    <AboutSection />
    <MissionSection />
    <WhyBuiltSection />
    <DifferenceSection />
    <VisionSection />
    <FinalCTASection />
    <DisclaimerBlock />
  </PageWrap>
);

export default AboutPage;
